import React from "react";
import type { SubmissionSummary, SubmissionSummaryItem } from "@core/reports/api/types";
import PreviewNameList from "@core/reports/components/PreviewNameList";
import {
  formatPeriodRangeLabel,
  formatReportTagDeadline,
  formatDeadlineCountdown,
} from "@features/report/tag/utils/reportTagDeadline";

type Props = {
  summary: SubmissionSummary | null | undefined;
  since?: string | null;
  until?: string | null;
  deadline?: string | null;
};

function BucketLine({
  label,
  submitted,
  outstanding,
}: {
  label: string;
  submitted: number;
  outstanding: number;
}) {
  return (
    <div className="small">
      <span className="fw-semibold">{label}:</span> {submitted} submitted ·{" "}
      {outstanding > 0 ? (
        <span className="fw-semibold text-danger">{outstanding} outstanding</span>
      ) : (
        <span>{outstanding} outstanding</span>
      )}
    </div>
  );
}

function DualLists({
  doneLabel,
  doneItems,
  outstandingItems,
}: {
  doneLabel: string;
  doneItems: SubmissionSummaryItem[];
  outstandingItems: SubmissionSummaryItem[];
}) {
  if (doneItems.length === 0 && outstandingItems.length === 0) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-2 mt-2">
      {outstandingItems.length > 0 ? (
        <div>
          <div className="fw-semibold small">Outstanding ({outstandingItems.length})</div>
          <PreviewNameList items={outstandingItems} />
        </div>
      ) : null}
      {doneItems.length > 0 ? (
        <div>
          <div className="fw-semibold small">
            {doneLabel} ({doneItems.length})
          </div>
          <PreviewNameList items={doneItems} />
        </div>
      ) : null}
    </div>
  );
}

function OutstandingByDistrict({ summary }: { summary: SubmissionSummary }) {
  if (!summary.outstanding_by_district?.length) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-2 mt-2">
      {summary.outstanding_by_district.map((district) => (
        <div key={district.id}>
          <div className="fw-semibold small">
            {district.name}
            {district.is_outstanding ? (
              <span className="fw-normal"> (outstanding)</span>
            ) : null}
          </div>
          {district.churches.length > 0 ? (
            <PreviewNameList items={district.churches} />
          ) : (
            <div className="small ps-1">
              {district.is_outstanding
                ? "District office not approved"
                : "No churches outstanding"}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Plain-text submission progress for the Status details panel.
 * District/nation show office vs children; church keeps templates bucket.
 */
export default function SubmissionSummaryControl({
  summary,
  since,
  until,
  deadline,
}: Props) {
  if (!summary) return null;

  const hasOffice = Boolean(summary.office);
  const hasChildren =
    Boolean(summary.districts) || Boolean(summary.churches);
  const hasLegacyTemplates = Boolean(summary.templates);
  const hasBuckets = hasOffice || hasChildren || hasLegacyTemplates;

  if (!hasBuckets) return null;

  const periodLabel = formatPeriodRangeLabel(since, until);
  const deadlineLabel = formatReportTagDeadline(deadline);
  const countdown = formatDeadlineCountdown(deadline);

  const officeDone = summary.office?.submitted_items ?? [];
  const officeOutstanding = summary.office?.outstanding_items ?? [];
  const templateDone = summary.templates?.submitted_items ?? [];
  const templateOutstanding = summary.templates?.outstanding_items ?? [];
  const churchDone = summary.churches?.approved_items ?? [];
  const churchOutstanding = summary.churches?.outstanding_items ?? [];
  const districtDone = summary.districts?.approved_items ?? [];
  const districtOutstanding = summary.districts?.outstanding_items ?? [];

  return (
    <div className="mt-2">
      <div className="small fw-semibold">
        {hasOffice && hasChildren ? "Office vs children progress" : "Submission summary"}
      </div>
      {(periodLabel || deadlineLabel) && (
        <div className="small text-muted mb-1">
          {periodLabel ? <>Period: {periodLabel}</> : null}
          {periodLabel && deadlineLabel ? " · " : null}
          {deadlineLabel ? (
            <>
              Deadline: {deadlineLabel}
              {countdown ? ` · ${countdown}` : null}
            </>
          ) : null}
        </div>
      )}
      <div className="ps-2 ms-1 border-start border-secondary-subtle">
        {summary.office ? (
          <>
            <BucketLine
              label="Office"
              submitted={summary.office.submitted}
              outstanding={summary.office.outstanding}
            />
            {!summary.outstanding_by_district?.length ? (
              <DualLists
                doneLabel="Submitted"
                doneItems={officeDone}
                outstandingItems={officeOutstanding}
              />
            ) : null}
          </>
        ) : null}
        {summary.templates && !summary.office ? (
          <>
            <BucketLine
              label="Templates"
              submitted={summary.templates.submitted}
              outstanding={summary.templates.outstanding}
            />
            <DualLists
              doneLabel="Submitted"
              doneItems={templateDone}
              outstandingItems={templateOutstanding}
            />
          </>
        ) : null}
        {summary.districts ? (
          <>
            <BucketLine
              label="Districts"
              submitted={summary.districts.submitted}
              outstanding={summary.districts.outstanding}
            />
            {!summary.outstanding_by_district?.length ? (
              <DualLists
                doneLabel="Approved"
                doneItems={districtDone}
                outstandingItems={districtOutstanding}
              />
            ) : null}
          </>
        ) : null}
        {summary.churches ? (
          <>
            <BucketLine
              label="Churches"
              submitted={summary.churches.submitted}
              outstanding={summary.churches.outstanding}
            />
            {!summary.outstanding_by_district?.length ? (
              <DualLists
                doneLabel="Approved"
                doneItems={churchDone}
                outstandingItems={churchOutstanding}
              />
            ) : null}
          </>
        ) : null}
        <OutstandingByDistrict summary={summary} />
      </div>
    </div>
  );
}
